// Stickers: send them from the picker in the message box; install free packs
// from the sticker store (the community directory of free packs at
// signalstickers.org, fetched and decrypted by our server); make your own
// from any picture. A sticker travels end-to-end encrypted like a photo, with
// a note of its pack so the receiver can add the pack too.
import { signal } from "@preact/signals";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import type { Msg, StickerRef } from "../db";
import { serverConfig } from "../features";
import { GifPanel } from "./Gifs";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";

export interface PackSticker {
  id: number;
  emoji?: string;
}
export interface Pack {
  id: string;
  key: string;
  title: string;
  author: string;
  cover: number;
  stickers: PackSticker[];
}
interface Mine {
  id: string;
  data: string; // data URL (WebP)
}
interface Store {
  packs: Pack[];
  recent: StickerRef[]; // pack stickers ({pack,key,id}) or yours ({emoji: "mine:<id>"})
  mine: Mine[];
}

// ---- local store (per account, on this device) ---------------------------------------

let account = "";
const key = () => "xmm.stickers." + account;
export const stickers = signal<Store>({ packs: [], recent: [], mine: [] });
export function loadStickers(jid: string) {
  account = jid;
  try {
    stickers.value = { packs: [], recent: [], mine: [], ...(JSON.parse(localStorage.getItem(key()) ?? "{}") as Partial<Store>) };
  } catch {
    stickers.value = { packs: [], recent: [], mine: [] };
  }
}
function save(s: Store) {
  stickers.value = s;
  try {
    localStorage.setItem(key(), JSON.stringify(s));
  } catch {
    toast("This device's storage is full: remove some of your stickers");
  }
}

export const stickerURL = (pack: string, packKey: string, id: number) => `/stickers/${pack}/${id}?key=${packKey}`;
const installed = (id: string) => stickers.value.packs.some((p) => p.id === id);

export async function fetchPack(id: string, packKey: string): Promise<Pack> {
  return api.stickerPack(id, packKey);
}
export function installPack(p: Pack) {
  if (installed(p.id)) return;
  save({ ...stickers.value, packs: [p, ...stickers.value.packs] });
  toast(`Added “${p.title}”`);
}
export function removePack(id: string) {
  save({ ...stickers.value, packs: stickers.value.packs.filter((p) => p.id !== id) });
}

/** Send a sticker: download the image (pack or yours) and send it as encrypted media. */
export async function sendSticker(jid: string, src: string, ref: StickerRef) {
  const e = engine.value;
  if (!e) return;
  const blob = await (await fetch(src)).blob();
  const bmp = await createImageBitmap(blob).catch(() => null);
  const media = { mime: blob.type || "image/webp", size: blob.size, blob, name: "sticker.webp", width: bmp?.width ?? 512, height: bmp?.height ?? 512 };
  bmp?.close();
  await e.sendDraft(jid, { body: "", kind: "sticker", media, sticker: ref.pack ? ref : { emoji: ref.emoji?.startsWith("mine:") ? undefined : ref.emoji } });
  const same = (r: StickerRef) => (r.pack ?? r.emoji) === (ref.pack ?? ref.emoji) && r.id === ref.id;
  save({ ...stickers.value, recent: [ref, ...stickers.value.recent.filter((r) => !same(r))].slice(0, 24) });
}

/** A picture -> a 512x512 WebP sticker (whole picture, transparent padding). */
async function makeSticker(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const s = Math.min(512 / bmp.width, 512 / bmp.height);
  const w = bmp.width * s;
  const h = bmp.height * s;
  c.getContext("2d")!.drawImage(bmp, (512 - w) / 2, (512 - h) / 2, w, h);
  bmp.close();
  for (const q of [0.85, 0.7, 0.5]) {
    const url = c.toDataURL("image/webp", q);
    if (url.length < 120_000) return url;
  }
  return c.toDataURL("image/webp", 0.4);
}

// ---- the picker (message box) -------------------------------------------------------------

export function StickerPicker({ jid, onClose }: { jid: string; onClose: () => void }) {
  const st = stickers.value;
  const [tab, setTab] = useState<string>(st.recent.length ? "recent" : st.packs[0]?.id ?? "store");
  const ref = useRef<HTMLDivElement>(null);
  const file = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onDown = (ev: MouseEvent) => ref.current && !ref.current.contains(ev.target as Node) && onClose();
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && onClose();
    setTimeout(() => document.addEventListener("mousedown", onDown));
    window.addEventListener("keydown", onKey);
    return () => (document.removeEventListener("mousedown", onDown), window.removeEventListener("keydown", onKey));
  }, []);
  const send = (src: string, r: StickerRef) => {
    onClose();
    void sendSticker(jid, src, r).catch((e) => toast("Couldn't send the sticker: " + (e as Error).message));
  };
  const srcOf = (r: StickerRef) => (r.pack && r.key && r.id !== undefined ? stickerURL(r.pack, r.key, r.id) : st.mine.find((m) => "mine:" + m.id === r.emoji)?.data);
  const pack = st.packs.find((p) => p.id === tab);
  const [gifs, setGifs] = useState(false);
  useEffect(() => void serverConfig().then((c) => setGifs(c.gifs === true)), []);
  return (
    <div class="sticker-picker" ref={ref} role="dialog" aria-label="Stickers">
      <nav class="sticker-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "recent"} class={tab === "recent" ? "on" : ""} onClick={() => setTab("recent")} aria-label="Recent stickers" title="Recent">
          <Icon name="timer" size={20} />
        </button>
        {gifs && (
          <button role="tab" aria-selected={tab === "gif"} class={"gif-tab" + (tab === "gif" ? " on" : "")} onClick={() => setTab("gif")} aria-label="GIFs" title="GIFs">
            GIF
          </button>
        )}
        <button role="tab" aria-selected={tab === "mine"} class={tab === "mine" ? "on" : ""} onClick={() => setTab("mine")} aria-label="My stickers" title="My stickers">
          <Icon name="person" size={20} />
        </button>
        {st.packs.map((p) => (
          <button key={p.id} role="tab" aria-selected={tab === p.id} class={tab === p.id ? "on" : ""} onClick={() => setTab(p.id)} aria-label={p.title} title={p.title}>
            <img src={stickerURL(p.id, p.key, p.cover)} alt="" loading="lazy" />
          </button>
        ))}
        <button role="tab" aria-selected={tab === "store"} class={"store-tab" + (tab === "store" ? " on" : "")} onClick={() => setTab("store")} aria-label="Sticker store" title="Get more stickers">
          <Icon name="plus" size={20} />
        </button>
      </nav>
      <div class="sticker-body">
        {tab === "recent" &&
          (st.recent.length ? (
            <Grid items={st.recent.flatMap((r) => (srcOf(r) ? [{ src: srcOf(r)!, label: r.emoji?.startsWith("mine:") ? "Your sticker" : r.emoji ?? "Sticker", ref: r }] : []))} onPick={send} />
          ) : (
            <p class="muted pad">Stickers you send show up here. Get some from the store (+).</p>
          ))}
        {tab === "mine" && (
          <>
            <Grid items={st.mine.map((m) => ({ src: m.data, label: "Your sticker", ref: { emoji: "mine:" + m.id } }))} onPick={send}>
              <button class="sticker-cell make" onClick={() => file.current?.click()} aria-label="Make a sticker from a picture" title="Make a sticker from a picture">
                <Icon name="image" size={24} />
                <small>New</small>
              </button>
            </Grid>
            <input
              ref={file}
              type="file"
              accept="image/*"
              hidden
              aria-label="Picture for a sticker"
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                if (f)
                  void makeSticker(f).then(
                    (data) => save({ ...stickers.value, mine: [{ id: crypto.randomUUID().slice(0, 8), data }, ...stickers.value.mine].slice(0, 60) }),
                    () => toast("Couldn't read that picture"),
                  );
              }}
            />
          </>
        )}
        {pack && (
          <>
            <div class="sticker-pack-head">
              <span class="ellipsis">
                <strong>{pack.title}</strong> <small class="muted">{pack.author}</small>
              </span>
              <button class="icon-btn small" onClick={() => (removePack(pack.id), setTab("store"))} aria-label={`Remove ${pack.title}`} title="Remove pack">
                <Icon name="trash" size={16} />
              </button>
            </div>
            <Grid items={pack.stickers.map((s) => ({ src: stickerURL(pack.id, pack.key, s.id), label: s.emoji ?? "Sticker", ref: { pack: pack.id, key: pack.key, id: s.id, emoji: s.emoji } }))} onPick={send} />
          </>
        )}
        {tab === "store" && <StickerStore onInstalled={(id) => setTab(id)} />}
        {tab === "gif" && <GifPanel jid={jid} onSent={onClose} />}
      </div>
    </div>
  );
}

function Grid({ items, onPick, children }: { items: { src: string; label: string; ref: StickerRef }[]; onPick: (src: string, r: StickerRef) => void; children?: preact.ComponentChildren }) {
  return (
    <div class="sticker-grid">
      {children}
      {items.map((it, i) => (
        <button key={i} class="sticker-cell" onClick={() => onPick(it.src, it.ref)} aria-label={`Send sticker ${it.label}`} title={it.label}>
          <img src={it.src} alt={it.label} loading="lazy" />
        </button>
      ))}
    </div>
  );
}

// ---- the store ---------------------------------------------------------------------------

interface StorePack {
  id: string;
  key: string;
  title: string;
  author: string;
  cover: number;
  animated?: boolean;
  featured?: boolean;
}

function StickerStore({ onInstalled }: { onInstalled: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [packs, setPacks] = useState<StorePack[] | null>(null);
  const [page, setPage] = useState(0);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<StorePack | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => input.current?.focus(), []);
  useEffect(() => {
    let live = true;
    const t = setTimeout(
      () =>
        void api.stickerStore(q, page).then(
          (r) => live && (setPacks((cur) => (page && cur ? [...cur, ...r.packs] : r.packs)), setMore(r.more), setError("")),
          (e) => live && setError((e as Error).message),
        ),
      page ? 0 : 250,
    );
    return () => ((live = false), clearTimeout(t));
  }, [q, page]);
  if (open) return <PackPreview p={open} onBack={() => setOpen(null)} onInstalled={onInstalled} />;
  return (
    <div class="sticker-store">
      <input ref={input} class="line-input" value={q} placeholder="Search free sticker packs" aria-label="Search sticker packs" onInput={(e) => (setQ(e.currentTarget.value), setPage(0))} />
      {error && <p class="error pad">{error}</p>}
      {!packs && !error && <p class="muted pad">Loading the store…</p>}
      <ul class="store-list">
        {packs?.map((p) => (
          <li key={p.id}>
            <button class="store-pack" onClick={() => setOpen(p)} aria-label={`Preview ${p.title}`}>
              <img src={stickerURL(p.id, p.key, p.cover)} alt="" loading="lazy" />
              <span class="grow ellipsis">
                <strong>{p.title}</strong>
                <small class="muted">
                  {p.author}
                  {p.animated ? " · animated" : ""}
                </small>
              </span>
            </button>
            {installed(p.id) ? (
              <span class="muted small">Added</span>
            ) : (
              <button
                class="icon-btn small"
                aria-label={`Add ${p.title}`}
                title="Add pack"
                onClick={() => void fetchPack(p.id, p.key).then((full) => (installPack(full), onInstalled(full.id)), (e) => toast((e as Error).message))}
              >
                <Icon name="plus" size={18} />
              </button>
            )}
          </li>
        ))}
        {packs && !packs.length && <li class="muted pad">No packs match “{q}”.</li>}
      </ul>
      {more && (
        <button class="link pad" onClick={() => setPage(page + 1)}>
          More packs
        </button>
      )}
      <p class="muted small pad">Free packs from the community directory signalstickers.org, shared by their creators.</p>
    </div>
  );
}

function PackPreview({ p, onBack, onInstalled }: { p: { id: string; key: string; title: string; author: string }; onBack: () => void; onInstalled: (id: string) => void }) {
  const [pack, setPack] = useState<Pack | null>(null);
  const [error, setError] = useState("");
  useEffect(() => void fetchPack(p.id, p.key).then(setPack, (e) => setError((e as Error).message)), [p.id]);
  return (
    <div class="sticker-store">
      <div class="sticker-pack-head">
        <button class="icon-btn small" onClick={onBack} aria-label="Back to the store">
          <Icon name="back" size={18} />
        </button>
        <span class="grow ellipsis">
          <strong>{p.title}</strong> <small class="muted">{p.author}</small>
        </span>
        {pack &&
          (installed(pack.id) ? (
            <span class="muted small">Added</span>
          ) : (
            <button class="chip on" onClick={() => (installPack(pack), onInstalled(pack.id))} aria-label={`Add ${p.title}`}>
              Add
            </button>
          ))}
      </div>
      {error && <p class="error pad">{error}</p>}
      {!pack && !error && <p class="muted pad">Opening the pack…</p>}
      {pack && (
        <div class="sticker-grid">
          {pack.stickers.map((s) => (
            <span key={s.id} class="sticker-cell">
              <img src={stickerURL(pack.id, pack.key, s.id)} alt={s.emoji ?? "Sticker"} loading="lazy" />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- a sticker someone sent: which pack, add it ---------------------------------------------

export function StickerInfo({ m, onClose }: { m: Msg; onClose: () => void }) {
  const r = m.sticker;
  const [pack, setPack] = useState<Pack | null>(null);
  useEffect(() => {
    if (r?.pack && r.key) void fetchPack(r.pack, r.key).then(setPack, () => {});
  }, [r?.pack]);
  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal sticker-info" role="dialog" aria-label="Sticker pack" onClick={(e) => e.stopPropagation()}>
        <header>
          <button class="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
          <h2>{pack ? pack.title : r?.pack ? "Sticker pack" : "Sticker"}</h2>
        </header>
        <div class="form-body">
          {!r?.pack && <p class="muted">This sticker isn't from a public pack.</p>}
          {r?.pack && !pack && <p class="muted">Opening the pack…</p>}
          {pack && (
            <>
              <p class="muted small">by {pack.author}</p>
              <div class="sticker-grid">
                {pack.stickers.map((s) => (
                  <span key={s.id} class="sticker-cell">
                    <img src={stickerURL(pack.id, pack.key, s.id)} alt={s.emoji ?? "Sticker"} loading="lazy" />
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
        {pack && (
          <footer class="forward-footer">
            <span class="muted">{pack.stickers.length} stickers</span>
            {installed(pack.id) ? (
              <button class="chip" onClick={() => (removePack(pack.id), onClose())}>
                Remove
              </button>
            ) : (
              <button class="chip on" onClick={() => (installPack(pack), onClose())}>
                Add pack
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}
